/**
 * Tipos para o sistema de campanhas com múltiplos itens de mensagem
 * 
 * Inclui suporte a mensagens interativas (imagem com botões)
 * e templates do WhatsApp Oficial
 */

// Tipos de item de mensagem disponíveis
export type CampaignItemType = 
  | 'text'           // Mensagem de texto simples
  | 'image'          // Imagem (jpg, png, webp)
  | 'video'          // Vídeo (mp4)
  | 'audio'          // Áudio (ogg, mp3)
  | 'ptv'            // Push-to-Talk Video (vídeo circular do WhatsApp)
  | 'gif'            // GIF animado
  | 'document'       // Documento (pdf, doc, xls, etc.)
  | 'delay'          // Temporizador entre mensagens
  | 'image_buttons'  // Imagem com botões de ação (WhatsApp interativo)
  | 'contact'        // Cartão de contato (vCard)
  | 'template';      // Template do WhatsApp Oficial

// Interface para botão de mensagem interativa
export interface CampaignButton {
  id: string;
  label: string;
}

// Interface para configuração de botões (JSON compatível)
export interface ButtonsConfig {
  buttons: CampaignButton[];
}

// Interface para parâmetros ORDER_DETAILS (PIX) - suporta variáveis do CSV
export interface OrderDetailsParams {
  pix_code: string;           // Código PIX copia-cola ou {{variavel}}
  merchant_name: string;      // Nome do recebedor ou {{variavel}}
  pix_key: string;            // Chave PIX ou {{variavel}}
  pix_key_type: 'CNPJ' | 'CPF' | 'EMAIL' | 'CELULAR' | 'ALEATORIA' | string;
  amount_value: string;       // Valor em reais (string para suportar variáveis)
  item_name?: string;         // Descrição do item ou {{variavel}}
  reference_id?: string;      // ID único ou {{variavel}}
}

// Interface para parâmetros de template
export interface TemplateParams {
  body_params?: string[];
  header_params?: string[];
  button_params?: Array<{ 
    type: 'button';
    sub_type: 'url';
    index: number; 
    parameters: Array<{ type: 'text'; text: string }>;
  }>;
  order_details_params?: OrderDetailsParams;
}

// Interface para item de mensagem da campanha
export interface MessageItem {
  id: string;
  item_type: CampaignItemType;
  sort_order: number;
  text_content?: string;
  media_url?: string;
  media_filename?: string;
  media_mime_type?: string;
  delay_seconds?: number;
  buttons_config?: ButtonsConfig | null;
  // Campos para tipo contact (cartão de contato vCard)
  contact_name?: string;
  contact_phone?: string;
  contact_business_description?: string;
  // Campos para tipo template (WhatsApp Oficial)
  template_name?: string;
  template_language?: string;
  template_params?: TemplateParams | null;
}

// Interface para registro no banco de dados (usa any para JSON)
export interface CampaignMessageItemRecord {
  id?: string;
  campaign_id: string;
  group_id?: string | null; // Novo: referência ao grupo de mensagens
  item_type: CampaignItemType;
  sort_order: number;
  text_content?: string | null;
  media_url?: string | null;
  media_filename?: string | null;
  media_mime_type?: string | null;
  delay_seconds?: number;
  // deno-lint-ignore no-explicit-any
  buttons_config?: any; // JSON compatível com Supabase
  // Campos para tipo contact (cartão de contato vCard)
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_business_description?: string | null;
  // Campos para tipo template (WhatsApp Oficial)
  template_name?: string | null;
  template_language?: string | null;
  // deno-lint-ignore no-explicit-any
  template_params?: any; // JSON compatível com Supabase
  created_at?: string;
}

// Interface para grupo de mensagens (novo sistema de diversificação)
export interface MessageGroup {
  id: string;
  name: string;
  items: MessageItem[];
  weight: number; // Peso para seleção aleatória ponderada (1-100)
}

// Interface para registro de grupo de mensagens no banco
export interface CampaignMessageGroupRecord {
  id?: string;
  campaign_id: string;
  name: string;
  sort_order: number;
  weight: number;
  created_at?: string;
}

// Mapeamento de tipos para tipos de mensagem do WhatsApp
export const itemTypeToMessageType: Record<CampaignItemType, string> = {
  text: 'text',
  image: 'image',
  video: 'video',
  audio: 'audio',
  ptv: 'ptv',
  gif: 'gif',
  document: 'document',
  delay: 'delay',
  image_buttons: 'image_buttons', // Tipo especial para imagem com botões
  contact: 'contact',             // Cartão de contato vCard
  template: 'template',           // Template do WhatsApp Oficial
};

// Mapeamento de tipos para ícones e labels
export const itemTypeConfig: Record<CampaignItemType, { 
  label: string; 
  emoji: string;
  allowCaption: boolean;
}> = {
  text: { label: 'Texto', emoji: '📝', allowCaption: false },
  image: { label: 'Imagem', emoji: '🖼️', allowCaption: true },
  video: { label: 'Vídeo', emoji: '🎬', allowCaption: true },
  audio: { label: 'Áudio', emoji: '🎵', allowCaption: false },
  ptv: { label: 'PTV', emoji: '⭕', allowCaption: false },
  gif: { label: 'GIF', emoji: '🎞️', allowCaption: true },
  document: { label: 'Documento', emoji: '📄', allowCaption: true },
  delay: { label: 'Delay', emoji: '⏱️', allowCaption: false },
  image_buttons: { label: 'Img c/ Botões', emoji: '🖼️🔘', allowCaption: true },
  contact: { label: 'Contato', emoji: '👤', allowCaption: false },
  template: { label: 'Template', emoji: '📋', allowCaption: false },
};

// Limite máximo de destinatários por campanha (prevenção de performance)
// Para disparos maiores, entrar em contato com o comercial UaizyChat
export const MAX_CAMPAIGN_RECIPIENTS = 1000;

// Tipos de mídia aceitos por cada tipo
export const allowedMimeTypes: Record<CampaignItemType, string[]> = {
  text: [],
  image: ['image/jpeg', 'image/png', 'image/webp'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
  audio: ['audio/ogg', 'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a'],
  ptv: ['video/mp4', 'video/webm'],
  gif: ['image/gif'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  delay: [],
  image_buttons: ['image/jpeg', 'image/png', 'image/webp'], // Mesmos tipos que imagem
  contact: [], // Não usa mídia
  template: [], // Template não usa mídia diretamente (header media é configurado no template)
};
